import { defineStore } from 'pinia';
import { hasCachedSession, hasSession, syncTenant } from '@/auth/sessionRuntime';

export interface KioskGroup {
  [groupId: string]: any;
}

export interface Kiosk {
  fingerprint: string | null;
  groups: KioskGroup;
}

export interface UserState {
  auth: any;
  user: any;
  tenant: any;
  kiosk: Kiosk | null;
}

export interface UserActions {
  setKioskFingerprint(txt: string): void;
  setKiosk(data: any, group_id: string): void;
  setTenant(tenant: any, skipApiCall?: boolean): Promise<void>;
  setUser(user: any): void;
  login(params: { auth: any; user?: any; tenants?: any[] }): Promise<void>;
  clearTenant(): Promise<void>;
  resetAuthState(): Promise<void>;
}

export const useUserStore = defineStore<'user', UserState, {
  haslogin: (state: UserState) => boolean;
}, UserActions>('user', {
  state: (): UserState => {
    return {
      auth: null,
      user: null,
      tenant: null,
      kiosk: null,
    };
  },
  getters: {
    haslogin: () => hasCachedSession(),
  },
  persist: {
    key: 'user',
    storage: localStorage,
    pick: ['auth', 'user', 'tenant', 'kiosk'],
  },
  actions: {
    setKioskFingerprint(txt) {
      if (!this.kiosk) {
        this.kiosk = {
          fingerprint: null,
          groups: {},
        };
      }
      this.kiosk.fingerprint = txt;
    },
    setKiosk(data, group_id) {
      if (!this.kiosk) {
        this.kiosk = {
          fingerprint: null,
          groups: {},
        };
      }
      if (!this.kiosk.groups) {
        this.kiosk.groups = {};
      }
      this.kiosk.groups[group_id] = data;
    },
    async setTenant(tenant, skipApiCall = false) {
      this.tenant = tenant;
      
      syncTenant(tenant?.ID);
      
      // 只有在用户主动切换时才调用API更新默认tenant
      if (await hasSession() && tenant && tenant.ID && !skipApiCall) {
        try {
          const { userApi } = await import('@arcubase/client-ts');
          await userApi.setDefaultTenant({ tenant_id: tenant.ID });
        } catch (error) {
          console.warn('Failed to set default tenant:', error);
        }
      }
    },
    setUser(user) {
      this.user = user;
    },
    async login({ auth, user = {}, tenants = [] }) {
      console.log('[user.ts login] START', { 
        hasUser: !!user, 
        hasAuth: !!auth,
        tenantCount: tenants?.length || 0,
      });
      
      // 登录时不设置tenant，让Entry API来决定
      this.auth = auth;
      this.user = user;
      this.tenant = null; // 关键：不预设tenant
      
      console.log('[user.ts login] State updated', {
        hasAuth: !!this.auth,
        hasUser: !!this.user,
        haslogin: hasCachedSession(),
      });
      
      syncTenant(null);
      
      console.log('[user.ts login] END - client synced');
    },
    async clearTenant() {
      this.tenant = null;
      
      syncTenant(null);
    },
    async resetAuthState() {
      this.auth = null;
      this.user = null;
      this.tenant = null;
      
      syncTenant(null);
    },
  },
});
